import {
  MutationFunction,
  QueryClient,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query'

import {
  BodyType,
  customInstance,
  ErrorType,
} from '@/features/auth/axios-config'
import { ErrorResponse, RequestListRoles } from '@/schema'

import { listRoles, SecondParameter } from './services'

/**
 * @summary List all roles with optional scope-level filtering
 */
export const usePostListRoles = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof listRoles>>,
      TError,
      { data: BodyType<RequestListRoles> },
      TContext
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof listRoles>>,
  TError,
  { data: BodyType<RequestListRoles> },
  TContext
> => {
  const mutationKey = ['useListRoles']

  const hasMutationKey =
    options?.mutation &&
    'mutationKey' in options.mutation &&
    options.mutation.mutationKey

  let resolvedOptions
  if (!options) {
    resolvedOptions = { mutation: { mutationKey }, request: undefined }
  } else if (hasMutationKey) {
    resolvedOptions = options
  } else {
    resolvedOptions = {
      ...options,
      mutation: { ...options.mutation, mutationKey },
    }
  }

  const { mutation: mutationOptions, request: requestOptions } = resolvedOptions

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof listRoles>>,
    { data: BodyType<RequestListRoles> }
  > = (props) => {
    const { data } = props ?? {}

    return listRoles(data, requestOptions)
  }

  return useMutation({ mutationFn, ...mutationOptions }, queryClient)
}
